/**
 * PricingCalculator — Calcolo prezzo in tempo reale nel form Store.
 * Mostra base + endpoint extra + add-on (NS, Awareness, MSSP).
 */
import React from 'react';

export default function PricingCalculator({
  prodotto,
  numEndpoint,
  awarenessSeats,
  awarenessEnabled,
  prodottoAwareness,
  nsEnabled,
  prodottoNS,
  msspEnabled,
  prodottoMSSP,
  gettoniTotali,
  isBundle,
}) {
  if (!prodotto) return null;

  const prezzoBase = parseFloat(prodotto.prezzo_base) || 0;
  const endpointInclusi = prodotto.numero_endpoint || 0;
  const endpointExtra = Math.max(0, numEndpoint - endpointInclusi);

  // Costo endpoint extra
  let costoEndpointExtra = 0;
  if (prodotto.scalabile && endpointExtra > 0) {
    const incremento = prodotto.incremento_scalabilita || 1;
    const blocchi = Math.ceil(endpointExtra / incremento);
    costoEndpointExtra = blocchi * parseFloat(prodotto.prezzo_scalabilita || 0);
  }

  // Per i bundle, gli add-on base sono già inclusi nel prezzo base
  // MA le quantità extra (utenze awareness, gettoni) oltre quelle incluse costano di più
  let costoNS = 0;
  let costoAwareness = 0;
  let costoMSSP = 0;
  let awarenessExtraLabel = '';
  let msspExtraLabel = '';

  if (!isBundle) {
    // Costo Network Surveillance
    if (nsEnabled && prodottoNS) {
      costoNS = parseFloat(prodottoNS.prezzo_base) || 0;
    }

    // Costo Awareness
    if (awarenessEnabled && prodottoAwareness) {
      costoAwareness = parseFloat(prodottoAwareness.prezzo_base) || 0;
      const seatsBase = prodottoAwareness.numero_awareness_seats || 10;
      if (awarenessSeats > seatsBase && prodottoAwareness.scalabile) {
        const seatsExtra = awarenessSeats - seatsBase;
        const blocchiFAwr = Math.ceil(seatsExtra / (prodottoAwareness.incremento_scalabilita || 10));
        costoAwareness += blocchiFAwr * parseFloat(prodottoAwareness.prezzo_scalabilita || 0);
      }
    }

    // Costo MSSP Monitoring
    if (msspEnabled && prodottoMSSP) {
      const gettoniBase = prodottoMSSP.numero_gettoni || 20;
      const prezzoPerGettone = parseFloat(prodottoMSSP.prezzo_base) / gettoniBase;
      costoMSSP = prezzoPerGettone * (gettoniTotali || gettoniBase);
    }
  } else {
    // Bundle: calcola costo EXTRA per utenze/gettoni oltre quelli inclusi

    // Extra awareness seats
    if (awarenessEnabled && prodottoAwareness && awarenessSeats > 0) {
      const seatsInclusi = prodotto.numero_awareness_seats || 0;
      const seatsExtra = awarenessSeats - seatsInclusi;
      if (seatsExtra > 0 && prodottoAwareness.scalabile) {
        const blocchi = Math.ceil(seatsExtra / (prodottoAwareness.incremento_scalabilita || 10));
        costoAwareness = blocchi * parseFloat(prodottoAwareness.prezzo_scalabilita || 0);
        awarenessExtraLabel = `+${seatsExtra} utenze extra`;
      }
    }

    // Extra gettoni
    if (msspEnabled && prodottoMSSP && gettoniTotali > 0) {
      const gettoniInclusi = prodotto.numero_gettoni || 0;
      const gettoniExtra = gettoniTotali - gettoniInclusi;
      if (gettoniExtra > 0) {
        const gettoniBase = prodottoMSSP.numero_gettoni || 20;
        const prezzoPerGettone = parseFloat(prodottoMSSP.prezzo_base) / gettoniBase;
        costoMSSP = prezzoPerGettone * gettoniExtra;
        msspExtraLabel = `+${gettoniExtra} gettoni extra`;
      }
    }
  }

  const subtotale = prezzoBase + costoEndpointExtra + costoNS + costoAwareness + costoMSSP;
  const iva = subtotale * 0.22;
  const totale = subtotale + iva;

  const fmt = (n) => n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
        Riepilogo prezzo
      </h4>

      <div className="space-y-2 text-sm">
        {/* Base */}
        <div className="flex justify-between">
          <span className="text-gray-600">Base ({prodotto.nome_prodotto})</span>
          <span className="font-medium">€{fmt(prezzoBase)}</span>
        </div>

        {/* Endpoint extra */}
        {costoEndpointExtra > 0 && (
          <div className="flex justify-between text-gray-500">
            <span>+{endpointExtra} endpoint extra</span>
            <span>€{fmt(costoEndpointExtra)}</span>
          </div>
        )}

        {/* Network Surveillance */}
        {costoNS > 0 && (
          <div className="flex justify-between text-gray-500">
            <span>Network Surveillance</span>
            <span>€{fmt(costoNS)}</span>
          </div>
        )}

        {/* Awareness */}
        {costoAwareness > 0 && (
          <div className="flex justify-between text-gray-500">
            <span>Awareness Training {awarenessExtraLabel || `(${awarenessSeats} utenze)`}</span>
            <span>+€{fmt(costoAwareness)}</span>
          </div>
        )}

        {/* MSSP Monitoring */}
        {costoMSSP > 0 && (
          <div className="flex justify-between text-gray-500">
            <span>Monitoraggio Cyber {msspExtraLabel || `(${gettoniTotali} gettoni)`}</span>
            <span>+€{fmt(costoMSSP)}</span>
          </div>
        )}

        <hr className="border-gray-300 my-1" />

        {/* Subtotale */}
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotale</span>
          <span className="font-medium">€{fmt(subtotale)}</span>
        </div>

        {/* IVA */}
        <div className="flex justify-between text-gray-500">
          <span>IVA 22%</span>
          <span>€{fmt(iva)}</span>
        </div>

        <hr className="border-gray-300 my-1" />

        {/* Totale */}
        <div className="flex justify-between text-lg font-bold text-gray-900">
          <span>Totale annuo</span>
          <span>€{fmt(totale)}</span>
        </div>
      </div>
    </div>
  );
}
